import React from "react";
import Barcode from "../Barcode";
import { SHELF_LABEL_SHEET } from "../../../constants/PickingConstant";
import { fitFontSizeMm, paginateLabels, slotPosition } from "../../../functions/shelfLabels";

/** The polyester sticker (SHELF_LABEL_SHEET): vak code, barcode and the bordje text on one label. */
const StickerLabel = ({ label, sheet }) => (
  <>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "3mm", width: "100%" }}>
      <span style={{ fontSize: "3mm", letterSpacing: "0.3mm" }}>VAK</span>
      <span style={{ fontSize: "15mm", fontWeight: 800, lineHeight: 1 }}>{label.location}</span>
    </div>
    <Barcode value={label.barcodeValue} moduleMm={sheet.barcodeModuleMm} heightMm={sheet.barcodeHeightMm} />
    <div style={{ width: "100%" }}>
      <div style={{ fontSize: "2.6mm", letterSpacing: "0.3mm" }}>HIER LIGT</div>
      <div style={{ fontSize: "4.6mm", fontWeight: 700, lineHeight: 1.15, maxHeight: "10.6mm", overflow: "hidden" }}>{label.title}</div>
      {(label.subtitle || label.zone) && (
        <div style={{ fontSize: "3mm", marginTop: "0.8mm" }}>{[label.subtitle, label.zone].filter(Boolean).join(" · ")}</div>
      )}
    </div>
  </>
);

/** The vak card, left in the card holder (SHELF_CARD_SHEET, small: SHELF_MAGNET_CARD_SHEET): vak code and barcode, nothing else. */
const VakCard = ({ label, sheet }) => (
  <>
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", width: "100%" }}>
      <span style={{ fontSize: `${sheet.cornerFontMm}mm`, letterSpacing: "0.3mm" }}>VAK</span>
      <span style={{ fontSize: `${sheet.codeFontMm}mm`, fontWeight: 800, lineHeight: 1 }}>{label.location}</span>
      <span style={{ fontSize: `${sheet.cornerFontMm}mm`, maxWidth: "20mm", overflow: "hidden", whiteSpace: "nowrap" }}>{label.zone || ""}</span>
    </div>
    <Barcode value={label.barcodeValue} moduleMm={sheet.barcodeModuleMm} heightMm={sheet.barcodeHeightMm} />
  </>
);

/**
 * The product card, right of the vak card (SHELF_PRODUCT_CARD_SHEET): what lies in the vak, the
 * weight biggest - a wrong weight is the mistake picking makes now. No barcode, so the scanner
 * can only ever read the vak card; the vak code in the corner tells which vak card it belongs next to.
 */
const ProductCard = ({ label, sheet }) => {
  const widthMm = sheet.labelWidthMm - 2 * sheet.paddingMm;
  // Without a weight the name may take the room the weight would have had
  const nameMaxMm = label.productWeight ? sheet.nameMaxMm : sheet.nameMaxMm * 1.4;
  const nameMm = fitFontSizeMm(label.productName, { widthMm, maxMm: nameMaxMm, lines: sheet.nameLines });
  const oneLine = sheet.nameLines === 1;
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", width: "100%" }}>
        <span style={{ fontSize: `${sheet.cornerFontMm}mm`, letterSpacing: "0.3mm" }}>HIER LIGT</span>
        <span style={{ fontSize: `${sheet.cornerFontMm * 1.5}mm`, fontWeight: 700 }}>{label.location}</span>
      </div>
      <div
        style={{
          fontSize: `${nameMm}mm`,
          fontWeight: 700,
          lineHeight: 1.1,
          maxWidth: "100%",
          maxHeight: `${1.1 * sheet.nameLines * nameMm}mm`,
          overflow: "hidden",
          ...(oneLine ? { whiteSpace: "nowrap", textOverflow: "ellipsis" } : {}),
        }}
      >
        {label.productName}
      </div>
      {!!label.productWeight && (
        <div style={{ fontSize: `${fitFontSizeMm(label.productWeight, { widthMm, maxMm: sheet.weightMaxMm })}mm`, fontWeight: 800, lineHeight: 1, whiteSpace: "nowrap" }}>
          {label.productWeight}
        </div>
      )}
    </>
  );
};

const CONTENT = { card: VakCard, product: ProductCard };

/**
 * Printable sheets of vak labels (HVC-43), plain DOM like the packing slip so react-to-print can
 * copy it as it is. Everything is positioned in millimetres against the sheet (SHELF_LABEL_SHEET
 * for the polyester sticker sheets, SHELF_CARD_SHEET and SHELF_PRODUCT_CARD_SHEET for cards to
 * cut); the print itself runs with `@page { size: A4; margin: 0 }` so the browser adds no margin
 * of its own.
 *
 * @param {Array} labels - from functions/shelfLabels.buildShelfLabels, through labelsForSheet
 * @param {number} startSlot - first free position on the first sheet (1-based)
 */
const ShelfLabelSheet = React.forwardRef(({ labels, startSlot = 1, sheet = SHELF_LABEL_SHEET }, ref) => {
  const perPage = sheet.columns * sheet.rows;
  const pages = paginateLabels(labels, { perPage, startSlot });
  const Content = CONTENT[sheet.kind] || StickerLabel;
  const isCard = !!CONTENT[sheet.kind];

  return (
    <div ref={ref}>
      {pages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          style={{
            position: "relative",
            width: `${sheet.pageWidthMm}mm`,
            height: `${sheet.pageHeightMm}mm`,
            overflow: "hidden",
            pageBreakAfter: pageIndex < pages.length - 1 ? "always" : "auto",
            breakAfter: pageIndex < pages.length - 1 ? "page" : "auto",
            fontFamily: "Arial, Helvetica, sans-serif",
            color: "#000",
            background: "#fff",
          }}
        >
          {page.map((label, slot) => {
            if (!label) return null;
            const { left, top } = slotPosition(slot, sheet);
            return (
              <div
                key={`${label.entryId || label.location}-${slot}`}
                data-vak={label.location}
                data-card={sheet.kind || "sticker"}
                style={{
                  position: "absolute",
                  left: `${left}mm`,
                  top: `${top}mm`,
                  width: `${sheet.labelWidthMm}mm`,
                  height: `${sheet.labelHeightMm}mm`,
                  boxSizing: "border-box",
                  padding: isCard ? `${sheet.paddingMm}mm` : "3mm 4mm",
                  // Cards are cut from plain paper: dashed lines to cut along
                  border: isCard ? "0.2mm dashed #999" : "none",
                  // Nothing runs into the card next to it
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  // On a card, away from the edges the rims of the holder cover
                  justifyContent: isCard ? "space-evenly" : "space-between",
                  textAlign: "center",
                }}
              >
                <Content label={label} sheet={sheet} />
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});

/** What react-to-print injects for this sheet: an A4 page without browser margins. */
export const SHELF_LABEL_PAGE_STYLE =
  "@page { size: A4 portrait; margin: 0; } html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }";

export default ShelfLabelSheet;
